import React from 'react';
import { Clock, CheckCircle, AlertTriangle, Shield, BarChart, Zap, Headphones } from 'lucide-react';

export const ServiceLevelAgreement: React.FC = () => {
  return (
    <div>
      {/* Hero Section */}
      <section className="pt-32 pb-16 bg-gradient-to-br from-primary-600 to-secondary-600 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <Clock className="h-16 w-16 text-white mx-auto mb-6" />
            <h1 className="text-4xl sm:text-5xl font-bold mb-6">
              Service Level Agreement
            </h1>
            <p className="text-xl text-primary-100 max-w-3xl mx-auto leading-relaxed">
              Our commitment to quality, reliability, and exceptional service for all website development and management services.
            </p>
            <p className="text-sm text-primary-200 mt-4">
              Last updated: January 1, 2025
            </p>
          </div>
        </div>
      </section>

      {/* Content */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="prose prose-lg max-w-none">
            
            {/* Introduction */}
            <div className="mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-6">Introduction</h2>
              <p className="text-gray-600 leading-relaxed mb-4">
                This Service Level Agreement ("SLA") outlines the terms and conditions under which OutstandingNet 
                ("Provider") will deliver website development, maintenance, and management services to the Client. 
                This SLA defines the service standards, response times, and mutual responsibilities to ensure 
                a successful partnership.
              </p>
              <p className="text-gray-600 leading-relaxed">
                By engaging our services, you agree to the terms outlined in this SLA. This document should be 
                read in conjunction with our Terms of Service and any project-specific agreements.
              </p>
            </div>

            {/* Service Availability */}
            <div className="mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-6 flex items-center">
                <Zap className="h-8 w-8 text-primary-600 mr-3" />
                Service Availability
              </h2>
              
              <div className="card p-6 mb-6 border border-gray-200 rounded-lg">
                <h3 className="text-xl font-semibold text-gray-900 mb-4">Website Uptime Guarantee</h3>
                <p className="text-gray-600 leading-relaxed mb-4">
                  We guarantee 99.9% uptime for all websites hosted on our recommended hosting platforms. 
                  This excludes scheduled maintenance windows and factors outside our control.
                </p>
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h4 className="font-semibold text-gray-900 mb-2">Uptime Calculation</h4>
                  <p className="text-sm text-gray-600">
                    Uptime is calculated on a monthly basis as: (Total Minutes in Month - Downtime Minutes) / Total Minutes in Month × 100
                  </p>
                </div>
              </div>

              <div className="card p-6 mb-6 border border-gray-200 rounded-lg">
                <h3 className="text-xl font-semibold text-gray-900 mb-4">Scheduled Maintenance</h3>
                <p className="text-gray-600 leading-relaxed mb-4">
                  Routine maintenance will be performed during low-traffic periods, typically between 11:00 PM and 5:00 AM Eastern Time.
                </p>
                <ul className="list-disc list-inside text-gray-600 space-y-2">
                  <li>Clients will receive at least 48 hours notice for scheduled maintenance</li>
                  <li>Emergency maintenance may be performed with shorter notice when necessary</li>
                  <li>Maintenance windows typically last 1-3 hours</li>
                </ul>
              </div>
            </div>

            {/* Support Response Times */}
            <div className="mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-6 flex items-center">
                <Headphones className="h-8 w-8 text-primary-600 mr-3" />
                Support Response Times
              </h2>
              
              <p className="text-gray-600 leading-relaxed mb-6">
                We categorize support requests based on severity to ensure appropriate response times:
              </p>
              
              <div className="overflow-x-auto">
                <table className="min-w-full border border-gray-200 rounded-lg">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Severity</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Description</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Response Time</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Resolution Target</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    <tr>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-red-100 text-red-800">
                          Critical
                        </span>
                      </td>
                      <td className="px-6 py-4">Website down, security breach, data loss</td>
                      <td className="px-6 py-4">4 hours</td>
                      <td className="px-6 py-4">8 hours</td>
                    </tr>
                    <tr>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-orange-100 text-orange-800">
                          High
                        </span>
                      </td>
                      <td className="px-6 py-4">Major functionality broken, payment issues</td>
                      <td className="px-6 py-4">8 hours</td>
                      <td className="px-6 py-4">12 hours</td>
                    </tr>
                    <tr>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-yellow-100 text-yellow-800">
                          Medium
                        </span>
                      </td>
                      <td className="px-6 py-4">Minor functionality issues, visual bugs</td>
                      <td className="px-6 py-4">24 hours</td>
                      <td className="px-6 py-4">24 hours</td>
                    </tr>
                    <tr>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-green-100 text-green-800">
                          Low
                        </span>
                      </td>
                      <td className="px-6 py-4">Content updates, general questions</td>
                      <td className="px-6 py-4">2 business days</td>
                      <td className="px-6 py-4">4 business days</td>
                    </tr>
                  </tbody>
                </table>
              </div>
              
              <p className="text-sm text-gray-500 mt-4">
                * Response times are during business hours (Monday-Friday, 9:00 AM - 5:00 PM Eastern Time)
              </p>
            </div>

            {/* Service Requests */}
            <div className="mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-6">Service Requests</h2>
              
              <h3 className="text-xl font-semibold text-gray-900 mb-4">Content Updates</h3>
              <p className="text-gray-600 leading-relaxed mb-4">
                Content update requests are handled according to the following schedule:
              </p>
              <ul className="list-disc list-inside text-gray-600 mb-6 space-y-2">
                <li>Standard content updates: Completed within 48 hours</li>
                <li>Minor design changes: Completed within 72 hours</li>
                <li>Major content revisions: Scheduled based on complexity</li>
              </ul>

              <h3 className="text-xl font-semibold text-gray-900 mb-4">Monthly Service Allocation</h3>
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 mb-6">
                <p className="text-gray-700 leading-relaxed mb-4">
                  <strong>Unlimited updates and changes are capped at 5 working hours per month</strong>, including regular maintenance and updates.
                </p>
                <ul className="list-disc list-inside text-gray-700 space-y-2">
                  <li>Hours reset monthly and do not roll over</li>
                  <li>Additional hours available at standard hourly rates</li>
                  <li>Major redesigns or new features require separate agreements</li>
                  <li>Emergency fixes and security updates are prioritized</li>
                </ul>
              </div>
            </div>

            {/* Performance Monitoring */}
            <div className="mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-6 flex items-center">
                <BarChart className="h-8 w-8 text-primary-600 mr-3" />
                Performance Monitoring
              </h2>
              
              <p className="text-gray-600 leading-relaxed mb-6">
                We proactively monitor the following performance metrics for all websites under our management:
              </p>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                <div className="card p-6 border border-gray-200 rounded-lg">
                  <h3 className="font-semibold text-gray-900 mb-3">Technical Performance</h3>
                  <ul className="list-disc list-inside text-gray-600 space-y-2">
                    <li>Page load speed (target: under 3 seconds)</li>
                    <li>Server response time</li>
                    <li>Uptime monitoring</li>
                    <li>Error rates and exceptions</li>
                    <li>Database performance</li>
                  </ul>
                </div>
                
                <div className="card p-6 border border-gray-200 rounded-lg">
                  <h3 className="font-semibold text-gray-900 mb-3">User Experience</h3>
                  <ul className="list-disc list-inside text-gray-600 space-y-2">
                    <li>Mobile responsiveness</li>
                    <li>Core Web Vitals</li>
                    <li>Conversion rates</li>
                    <li>User engagement metrics</li>
                    <li>Form completion rates</li>
                  </ul>
                </div>
              </div>
              
              <h3 className="text-xl font-semibold text-gray-900 mb-4">Reporting</h3>
              <p className="text-gray-600 leading-relaxed mb-4">
                Clients on maintenance plans receive:
              </p>
              <ul className="list-disc list-inside text-gray-600 mb-6 space-y-2">
                <li>Monthly performance reports</li>
                <li>Quarterly comprehensive analytics review</li>
                <li>Real-time access to monitoring dashboard</li>
                <li>Immediate notification of critical issues</li>
              </ul>
            </div>

            {/* Security */}
            <div className="mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-6 flex items-center">
                <Shield className="h-8 w-8 text-primary-600 mr-3" />
                Security
              </h2>
              
              <p className="text-gray-600 leading-relaxed mb-6">
                Our security commitments include:
              </p>
              
              <div className="card p-6 mb-6 border border-gray-200 rounded-lg">
                <h3 className="text-xl font-semibold text-gray-900 mb-4">Proactive Security Measures</h3>
                <ul className="list-disc list-inside text-gray-600 space-y-2">
                  <li>Weekly security scans and vulnerability assessments</li>
                  <li>Malware monitoring and removal</li>
                  <li>Firewall configuration and maintenance</li>
                  <li>Regular software and plugin updates</li>
                  <li>SSL certificate management and renewal</li>
                  <li>Secure user authentication implementation</li>
                </ul>
              </div>

              <div className="card p-6 mb-6 border border-gray-200 rounded-lg">
                <h3 className="text-xl font-semibold text-gray-900 mb-4">Backup Policy</h3>
                <ul className="list-disc list-inside text-gray-600 space-y-2">
                  <li>Daily automated backups</li>
                  <li>Secure off-site backup storage</li>
                  <li>30-day backup retention</li>
                  <li>Pre-update backup snapshots</li>
                  <li>Disaster recovery testing</li>
                </ul>
              </div>

              <div className="card p-6 mb-6 border border-gray-200 rounded-lg">
                <h3 className="text-xl font-semibold text-gray-900 mb-4">Incident Response</h3>
                <p className="text-gray-600 leading-relaxed mb-4">
                  In the event of a security incident:
                </p>
                <ul className="list-disc list-inside text-gray-600 space-y-2">
                  <li>Immediate containment measures</li>
                  <li>Client notification within 4 hours</li>
                  <li>Root cause analysis</li>
                  <li>Remediation and recovery</li>
                  <li>Post-incident report and recommendations</li>
                </ul>
              </div>
            </div>

            {/* Client Responsibilities */}
            <div className="mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-6 flex items-center">
                <CheckCircle className="h-8 w-8 text-primary-600 mr-3" />
                Client Responsibilities
              </h2>
              
              <p className="text-gray-600 leading-relaxed mb-4">
                To ensure optimal service delivery, clients are responsible for:
              </p>
              
              <ul className="list-disc list-inside text-gray-600 mb-6 space-y-2">
                <li>Providing accurate and timely information for service requests</li>
                <li>Maintaining current contact information</li>
                <li>Promptly reviewing and approving deliverables</li>
                <li>Securing access credentials provided by OutstandingNet</li>
                <li>Timely payment of invoices according to agreed terms</li>
                <li>Providing content and assets in requested formats</li>
                <li>Notifying OutstandingNet of any observed issues promptly</li>
                <li>Adhering to agreed project timelines and milestones</li>
              </ul>
            </div>

            {/* Service Credits */}
            <div className="mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-6">Service Credits</h2>
              
              <p className="text-gray-600 leading-relaxed mb-4">
                If we fail to meet our SLA commitments, clients may be eligible for service credits:
              </p>
              
              <div className="overflow-x-auto">
                <table className="min-w-full border border-gray-200 rounded-lg">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">SLA Violation</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Credit</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Conditions</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    <tr>
                      <td className="px-6 py-4">Uptime below 99.9%</td>
                      <td className="px-6 py-4">5% of monthly fee</td>
                      <td className="px-6 py-4">Per 1% below guarantee</td>
                    </tr>
                    <tr>
                      <td className="px-6 py-4">Critical issue response time exceeded</td>
                      <td className="px-6 py-4">10% of monthly fee</td>
                      <td className="px-6 py-4">Per incident</td>
                    </tr>
                    <tr>
                      <td className="px-6 py-4">High priority issue response time exceeded</td>
                      <td className="px-6 py-4">5% of monthly fee</td>
                      <td className="px-6 py-4">Per incident</td>
                    </tr>
                    <tr>
                      <td className="px-6 py-4">Content update deadline missed</td>
                      <td className="px-6 py-4">2% of monthly fee</td>
                      <td className="px-6 py-4">Per day of delay</td>
                    </tr>
                  </tbody>
                </table>
              </div>
              
              <div className="mt-6 bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                <h3 className="font-semibold text-gray-900 mb-2 flex items-center">
                  <AlertTriangle className="h-5 w-5 text-yellow-600 mr-2" />
                  Credit Request Process
                </h3>
                <p className="text-sm text-gray-600">
                  To request service credits, clients must submit a claim within 7 days of the SLA violation. 
                  Credits will be applied to the next billing cycle and cannot exceed 30% of the monthly fee.
                </p>
              </div>
            </div>

            {/* SLA Exclusions */}
            <div className="mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-6 flex items-center">
                <AlertTriangle className="h-8 w-8 text-primary-600 mr-3" />
                SLA Exclusions
              </h2>
              
              <p className="text-gray-600 leading-relaxed mb-4">
                This SLA does not apply to performance issues caused by:
              </p>
              
              <ul className="list-disc list-inside text-gray-600 mb-6 space-y-2">
                <li>Force majeure events (natural disasters, acts of war, etc.)</li>
                <li>Third-party service provider outages</li>
                <li>Client-initiated changes or modifications</li>
                <li>Scheduled maintenance windows</li>
                <li>Client's failure to meet responsibilities</li>
                <li>Issues with client-provided content or assets</li>
                <li>Domain name or DNS issues outside our control</li>
                <li>Suspension due to violation of Terms of Service</li>
                <li>Non-payment of invoices</li>
              </ul>
            </div>

            {/* SLA Modifications */}
            <div className="mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-6">SLA Modifications</h2>
              
              <p className="text-gray-600 leading-relaxed mb-4">
                OutstandingNet reserves the right to modify this SLA with 30 days' notice to clients. 
                Material changes will be communicated via email and published on our website.
              </p>
            </div>

            {/* Contact Information */}
            <div className="card p-8 bg-gray-50">
              <h2 className="text-3xl font-bold text-gray-900 mb-6">Support Contact Information</h2>
              <p className="text-gray-600 leading-relaxed mb-6">
                For support requests or questions about this SLA, please contact us:
              </p>
              <div className="space-y-4">
                <div className="flex items-center">
                  <span className="text-gray-700">📧 support@outstandingnet.com</span>
                </div>
                <div className="flex items-center">
                  <span className="text-gray-700">📞 (586) 248-4115</span>
                </div>
                <div className="flex items-center">
                  <span className="text-gray-700">⏰ Monday-Friday, 9:00 AM - 5:00 PM Eastern Time</span>
                </div>
                <div className="flex items-start">
                  <div className="text-gray-700">
                    📍 11908 Cedarwood Dr<br />
                    &nbsp;&nbsp;&nbsp;&nbsp;Shelby Township, MI 48315
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};